import React, { useEffect, useMemo, useState } from "react";


/** 10-question geography quiz (English) */
const QUESTIONS = [
  {
    q: "Warm-up: Which U.S. state is directly north of South Carolina?",
    options: ["Virginia", "South Carolina", "North Carolina", "Georgia"],
    a: 2,
    expl: "North Carolina borders South Carolina to the north.",
  },
  {
    q: "Which of these countries is landlocked (no coastline on the world ocean)?",
    options: ["Indonesia", "Norway", "Argentina", "Niger"],
    a: 3,
    expl: "Niger is landlocked; the others have coastlines.",
  },
  {
    q: "Which of these countries has a commercial airport?",
    options: ["Iceland", "Monaco", "Andorra", "San Marino"],
    a: 0,
    expl: "Iceland has several airports; Monaco, Andorra, and San Marino do not.",
  },
  {
    q: "The capital city of which country is Kathmandu?",
    options: ["Afghanistan", "Pakistan", "Nepal", "Bhutan"],
    a: 2,
    expl: "Kathmandu is the capital of Nepal.",
  },
  {
    q: "Which river is the longest in Africa?",
    options: ["Nile", "Congo", "Niger", "Zambezi"],
    a: 0,
    expl: "The Nile is generally considered Africa’s longest river.",
  },
  {
    q: "Which country lies in both Europe and Asia?",
    options: ["Spain", "Turkey", "Morocco", "Ireland"],
    a: 1,
    expl: "Turkey spans both continents.",
  },
  {
    q: "How many states are there in Australia?",
    options: ["Four", "Five", "Six", "Seven"],
    a: 2,
    expl: "Australia is comprised of six states and two major internal territories. The six states are: New South Wales, Victoria, Queensland, Western Australia, South Australia, and Tasmania. The two major territories are the Australian Capital Territory (ACT) and the Northern Territory (NT).",
  },
  {
    q: "Which country’s flag is maroon with a serrated white band?",
    options: ["Qatar", "United Arab Emirates", "Bahrain", "Oman"],
    a: 0,
    expl: "That distinctive maroon/white serrated flag belongs to Qatar.",
  },
  {
    q: "Which of these is Brazil’s largest city by population?",
    options: ["São Paulo", "Brasília", "Rio de Janeiro"],
    a: 0,
    expl: "São Paulo is the largest; Brasília is the capital.",
  },
  {
    q: "Final one: Which country does NOT share a land border with Switzerland?",
    options: ["Germany", "Sweden", "France", "Italy"],
    a: 1,
    expl: "Switzerland borders Germany, France, Italy, Austria, and Liechtenstein—but not Sweden.",
  },
];

export default function GeographyQuiz({ title = "Geography Test" }) {
  // ---- helpers ----
  const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  // ---- state ----
  const [order, setOrder] = useState(() =>
    shuffle([...Array(QUESTIONS.length).keys()])
  );
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState(Array(QUESTIONS.length).fill(null));
  const [checked, setChecked] = useState(Array(QUESTIONS.length).fill(false));
  const [score, setScore] = useState(0);

  const total = QUESTIONS.length;
  const qIdx = order[idx];
  const q = QUESTIONS[qIdx];
  const picked = answers[qIdx];

  // keyboard: N/P/R
  useEffect(() => {
    const onKey = (e) => {
      const k = e.key.toLowerCase();
      if (k === "n") next();
      if (k === "p") prev();
      if (k === "r") restart();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, order, answers, checked, score]);

  const progressPct = Math.round(((idx + 1) / total) * 100);
  const finishedPct = Math.round(
    (checked.filter(Boolean).length / total) * 100
  );

  // ---- actions ----
  function selectOption(i) {
    setAnswers((prev) => {
      const clone = [...prev];
      clone[qIdx] = i;
      return clone;
    });
  }
  function submitCurrent() {
    if (checked[qIdx]) return;
    setChecked((prev) => {
      const clone = [...prev];
      clone[qIdx] = true;
      return clone;
    });
    if (picked === q.a) setScore((s) => s + 1);
  }
  function next() {
    if (idx < total - 1) setIdx((i) => i + 1);
  }
  function prev() {
    if (idx > 0) setIdx((i) => i - 1);
  }
  function restart() {
    setOrder(shuffle([...Array(QUESTIONS.length).keys()]));
    setIdx(0);
    setAnswers(Array(QUESTIONS.length).fill(null));
    setChecked(Array(QUESTIONS.length).fill(false));
    setScore(0);
  }

  return (
    <div className="card shadow-sm border-0 geo-quiz" style={{ maxWidth: 900 }}>
      <div className="card-body">
        <h2 className="h5 mb-1">{title}</h2>
        <div className="text-muted small mb-3">
          10 questions · press <code>N</code> (next), <code>P</code> (prev),{" "}
          <code>R</code> (restart)
        </div>

        {/* header & progress */}
        <div className="d-flex flex-wrap align-items-center justify-content-between mb-2">
          <div>
            <span className="badge text-bg-light">
              Question <strong>{idx + 1}</strong> / <strong>{total}</strong>
            </span>{" "}
            <span className="badge text-bg-light ms-1">
              Score: <strong>{score}</strong>
            </span>
          </div>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={restart}
            title="Restart (R)"
          >
            Restart
          </button>
        </div>

        <div className="progress mb-2" style={{ height: 8 }}>
          <div
            className="progress-bar"
            role="progressbar"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <div className="progress mb-3" style={{ height: 8 }}>
          <div
            className="progress-bar bg-success"
            role="progressbar"
            style={{ width: `${finishedPct}%` }}
          />
        </div>

        {/* question */}
        <div className="mb-2 fw-semibold">
          Q{idx + 1}: {q.q}
        </div>

        {/* options (div buttons so clicks never get swallowed by a parent form) */}
        <div className="list-group">
          {q.options.map((opt, i) => {
            const isPicked = picked === i;
            const isChecked = checked[qIdx];
            const isCorrect = i === q.a;
            const showSuccess = isChecked && isPicked && isCorrect;
            const showWrong = isChecked && isPicked && !isCorrect;

            return (
              <div
                key={i}
                role="button"
                tabIndex={0}
                className="list-group-item list-group-item-action d-flex align-items-center justify-content-between"
                style={{
                  cursor: isChecked ? "default" : "pointer",
                  borderRadius: 8,
                  backgroundColor: showSuccess
                    ? "var(--bs-success-bg-subtle,#e9f7ef)"
                    : showWrong
                    ? "var(--bs-danger-bg-subtle,#fdecef)"
                    : "var(--bs-body-bg,#fff)",
                  borderColor: showSuccess
                    ? "var(--bs-success,#198754)"
                    : showWrong
                    ? "var(--bs-danger,#dc3545)"
                    : "var(--bs-border-color,#e5e7eb)",
                  marginBottom: 8,
                }}
                onClick={() => !isChecked && selectOption(i)}
                onKeyDown={(e) => {
                  if (!isChecked && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    selectOption(i);
                  }
                }}
              >
                <span>{opt}</span>
                {isChecked && (
                  <span
                    className={
                      "ms-2 badge " +
                      (isCorrect ? "text-bg-success" : "text-bg-danger")
                    }
                  >
                    {isCorrect ? "Correct" : "Incorrect"}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* actions */}
        <div className="d-flex gap-2 mt-3">
          <button type="button" className="btn btn-primary" onClick={submitCurrent}>
            {checked[qIdx] ? "Submitted" : "Submit"}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={prev}
            disabled={idx === 0}
          >
            Prev (P)
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={next}
            disabled={idx === total - 1}
          >
            Next (N)
          </button>
        </div>

        {/* explanation for current */}
        {checked[qIdx] && (
          <div
            className="mt-3 p-3 rounded-3 border"
            style={{ background: "#f8fafc", borderColor: "#cbd5e1" }}
          >
            <div className="fw-semibold mb-1">Answer &amp; Explanation</div>
            <div className="small">
              Correct answer: <strong>{q.options[q.a]}</strong>
            </div>
            {q.expl && <div className="small text-muted mt-1">{q.expl}</div>}
          </div>
        )}

        {/* final results */}
        {checked.every(Boolean) && (
          <>
            <hr />
            <ResultsPanel score={score} total={total} answers={answers} order={order} />
          </>
        )}
      </div>
    </div>
  );
}

/** Results list with explanations */
function ResultsPanel({ score, total, answers, order }) {
  const pct = useMemo(() => Math.round((score / total) * 100), [score, total]);

  return (
    <div>
      <h3 className="h5">Results</h3>
      <div className="mb-2">
        <strong>Your score: {score} / {total}</strong> ({pct}%)
      </div>

      <div className="list-group">
        {order.map((qIdx, i) => {
          const QQ = QUESTIONS[qIdx];
          const got = answers[qIdx] === QQ.a;
          return (
            <div
              key={i}
              className="list-group-item"
              style={{
                borderRadius: 8,
                backgroundColor: got
                  ? "var(--bs-success-bg-subtle,#e9f7ef)"
                  : "var(--bs-danger-bg-subtle,#fdecef)",
                borderColor: got ? "var(--bs-success,#198754)" : "var(--bs-danger,#dc3545)",
                marginBottom: 8,
              }}
            >
              <div className="fw-semibold">Q{i + 1}: {QQ.q}</div>
              <div className="small">
                <em>{got ? "✓ Correct" : "✗ Incorrect"}</em> — Answer:{" "}
                <strong>{QQ.options[QQ.a]}</strong>
              </div>
              {QQ.expl && <div className="small text-muted mt-1">{QQ.expl}</div>}
            </div>
          );
        })}
      </div>

      <p className="text-muted small mt-3">
        For fun and learning — not an official assessment.
      </p>
    </div>
  );
}